import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ArrowLeft, LogOut, Search, X, MapPin, Users, Map,
  ChevronDown, ChevronUp, RefreshCw, Info, Stethoscope, Building2, Filter
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import UserMenu from '../components/UserMenu';
import { apiFetch } from '../utils/api';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({ iconRetinaUrl: markerIcon2x, iconUrl: markerIcon, shadowUrl: markerShadow });

/**
 * UK Population Explorer — Leaflet map over Census 2021 Output-Area
 * population data, used for territory/site planning against the
 * surgeons database.
 *
 * Click the map (or search a postcode) → the page asks
 * /api/population/* for every Output Area within the radius and draws
 * population markers; the NS-SeC dropdown filters to one socioeconomic
 * class (NSSEC_CLASSES maps the UI labels to the Census L-codes). The
 * same click also overlays vet clinics from /api/surgeons-db so staff
 * can see coverage vs demand; clinic/surgeon popups deep-link into
 * SurgeonsDatabase via sessionStorage hand-off (no router exists).
 *
 * Note: Leaflet's default marker icons are broken under webpack (the
 * bundler can't resolve the image URLs automatically) — the
 * delete _getIconUrl + mergeOptions dance above is the standard fix.
 * Backend calls go through apiFetch (adds the Bearer token); the two
 * api.postcodes.io calls stay as raw fetch — they are external and
 * must not carry the VetFlow JWT.
 */

const NSSEC_CLASSES = [
  { id: 'all', label: 'All Classes', description: 'Total population' },
  { id: '1', label: 'Higher professional', description: 'L1-L3: Higher managerial' },
  { id: '2', label: 'Lower professional', description: 'L4-L6: Lower managerial' },
  { id: '3', label: 'Intermediate', description: 'L7: Intermediate occupations' },
  { id: '4', label: 'Small employers', description: 'L8-L9: Small employers' },
  { id: '5', label: 'Lower supervisory', description: 'L10-L11: Lower supervisory' },
  { id: '6', label: 'Semi-routine', description: 'L12: Semi-routine occupations' },
  { id: '7', label: 'Routine', description: 'L13: Routine occupations' },
  { id: '8', label: 'Unemployed', description: 'L14: Never worked/unemployed' },
  { id: '9', label: 'Students', description: 'L15: Full-time students' },
];

const KM_TO_MI = 0.621371;
const MI_TO_KM = 1.60934;

const formatPop = (n) => {
  if (!n && n !== 0) return '—';
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return (n / 1000).toFixed(0) + 'K';
  return n.toLocaleString();
};

// ── Leaflet map component ─────────────────────────────────────────────────────
const LeafletMap = ({ selectedLocation, radius, onMapClick, populationAreas, clinicMarkers, showClinics, vetGroups, ownershipList, onClinicNavigate, onSurgeonNavigate }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerLayerRef = useRef(null);
  const clinicLayerRef = useRef(null);
  const circleRef = useRef(null);
  const selectedMarkerRef = useRef(null);
  const onClickRef = useRef(onMapClick);

  useEffect(() => { onClickRef.current = onMapClick; }, [onMapClick]);
  const onClinicNavigateRef = useRef(onClinicNavigate);
  useEffect(() => { onClinicNavigateRef.current = onClinicNavigate; }, [onClinicNavigate]);
  const onSurgeonNavigateRef = useRef(onSurgeonNavigate);
  useEffect(() => { onSurgeonNavigateRef.current = onSurgeonNavigate; }, [onSurgeonNavigate]);

  // Init Leaflet map once after mount
  useEffect(() => {
    if (mapInstanceRef.current || !mapRef.current) return;
    const map = L.map(mapRef.current, { zoomControl: true }).setView([54.5, -2.5], 6);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
    }).addTo(map);
    map.on('click', (e) => onClickRef.current(e.latlng.lat, e.latlng.lng));
    markerLayerRef.current = L.layerGroup().addTo(map);
    clinicLayerRef.current = L.layerGroup().addTo(map);
    mapInstanceRef.current = map;
    setTimeout(() => map.invalidateSize(), 100);
    setTimeout(() => map.invalidateSize(), 500);
    return () => {
      map.remove();
      mapInstanceRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Update selected location marker + radius circle
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;
    if (selectedMarkerRef.current) { map.removeLayer(selectedMarkerRef.current); selectedMarkerRef.current = null; }
    if (circleRef.current) { map.removeLayer(circleRef.current); circleRef.current = null; }
    if (selectedLocation) {
      selectedMarkerRef.current = L.circleMarker([selectedLocation.lat, selectedLocation.lng], {
        radius: 10, fillColor: '#EF4444', color: '#fff', weight: 3, opacity: 1, fillOpacity: 1,
      }).addTo(map).bindPopup('Selected Location').openPopup();
      circleRef.current = L.circle([selectedLocation.lat, selectedLocation.lng], {
        color: '#3B82F6', fillColor: '#3B82F6', fillOpacity: 0.08, weight: 2, radius: radius * 1000,
      }).addTo(map);
      map.setView([selectedLocation.lat, selectedLocation.lng], 10, { animate: true });
    }
  }, [selectedLocation, radius]);

  // Update population area markers
  useEffect(() => {
    const layer = markerLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    populationAreas.forEach((area) => {
      L.circleMarker([area.lat, area.lng], {
        radius: 6, fillColor: '#22C55E', color: '#fff', weight: 2, opacity: 1, fillOpacity: 0.9,
      }).addTo(layer).bindPopup(
`<div style="font-family:system-ui,sans-serif;min-width:120px">
          <b style="font-size:14px">${area.outcode}</b><br/>
          <span style="color:#16a34a;font-weight:600">${formatPop(area.filtered_population ?? area.population)}</span><br/>
          <span style="color:#888;font-size:12px">${area.distance_km?.toFixed(1)} km</span>
        </div>`
      );
    });
  }, [populationAreas]);

  // Update clinic overlay markers
  useEffect(() => {
    const layer = clinicLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    if (!showClinics) return;
    clinicMarkers.forEach((clinic) => {
      if (!clinic.lat || !clinic.lng) return;
      const clinicHubs = (vetGroups || []).filter(h => (clinic.hub_ids || []).includes(h.id));
      const hubLabel = clinicHubs.map(h => h.name).join(', ');
      const ownerName = (ownershipList || []).find(o => o.id === clinic.ownership_id)?.name || '';
      const isOptivet = ownerName.toLowerCase().includes('optivet');
      const markerColor = isOptivet ? '#1d4ed8' : '#dc2626';
      const surgeons = clinic.surgeons || [];
      const surgeonsHtml = surgeons.length
        ? surgeons.map(s => {
            const fullName = `${s.first_name} ${s.last_name}`.trim();
            const encodedName = encodeURIComponent(fullName);
            return `<div style="padding:3px 0;border-bottom:1px solid #f0f0f0;font-size:12px">
              <span
                onclick="window.dispatchEvent(new CustomEvent('vetflow-open-surgeon',{detail:{id:'${s.id}',name:decodeURIComponent('${encodedName}')}}))" 
                style="font-weight:600;color:#1e40af;cursor:pointer;text-decoration:underline"
              >${s.title ? s.title + ' ' : ''}${fullName}</span><br/>
              <span style="color:#6b7280">${s.specialty || ''}</span><br/>
              <span style="color:#9ca3af;font-size:11px">${s.qualifications || ''}</span>
            </div>`;
          }).join('')
        : '<div style="color:#9ca3af;font-size:12px">No surgeons listed</div>';

      const icon = L.divIcon({
        className: '',
        html: `<div style="
          width:28px;height:28px;border-radius:50%;
          background:${markerColor};border:3px solid #fff;
          box-shadow:0 2px 6px rgba(0,0,0,0.35);
          display:flex;align-items:center;justify-content:center;
          font-size:14px;font-weight:900;color:#fff;line-height:1;
        ">+</div>`,
        iconSize: [28, 28],
        iconAnchor: [14, 14],
        popupAnchor: [0, -16],
      });

      const marker = L.marker([clinic.lat, clinic.lng], { icon }).addTo(layer);
      marker.bindPopup(`
          <div style="font-family:system-ui,sans-serif;min-width:210px;max-width:260px">
            <div style="background:${markerColor};color:#fff;margin:-14px -20px 10px;padding:10px 14px;border-radius:4px 4px 0 0">
              <div style="font-weight:700;font-size:14px">${clinic.name}</div>
              <div style="font-size:11px;opacity:0.9;margin-top:2px">${hubLabel}</div>
            </div>
            <div style="font-size:12px;color:#374151;margin-bottom:6px">
              <div style="margin-bottom:3px">📍 ${clinic.address}</div>
              <div style="margin-bottom:3px">📞 ${clinic.phone}</div>
              <div>✉️ ${clinic.email}</div>
            </div>
            <div style="font-weight:700;font-size:12px;color:#374151;margin:8px 0 4px;text-transform:uppercase;letter-spacing:0.05em">Surgeons</div>
            ${surgeonsHtml}
            <div style="margin-top:10px;padding-top:8px;border-top:1px solid #e5e7eb">
              <button onclick="window.dispatchEvent(new CustomEvent('vetflow-open-clinic',{detail:'${clinic.id}'}))"
                style="width:100%;padding:6px 10px;background:#16a34a;color:#fff;border:none;border-radius:6px;font-size:12px;font-weight:600;cursor:pointer">
                View in Surgeons Database →
              </button>
            </div>
          </div>
        `, { maxWidth: 280 });
      let hoverTimer = null;
      marker.on('mouseover', () => { hoverTimer = setTimeout(() => marker.openPopup(), 1000); });
      marker.on('mouseout',  () => { clearTimeout(hoverTimer); });
    });
  }, [clinicMarkers, showClinics, vetGroups, ownershipList]);

  // Listen for popup button clicks and forward to React
  useEffect(() => {
    const handler = (e) => { if (onClinicNavigateRef.current) onClinicNavigateRef.current(e.detail); };
    window.addEventListener('vetflow-open-clinic', handler);
    return () => window.removeEventListener('vetflow-open-clinic', handler);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (onSurgeonNavigateRef.current) onSurgeonNavigateRef.current(e.detail?.id, e.detail?.name);
    };
    window.addEventListener('vetflow-open-surgeon', handler);
    return () => window.removeEventListener('vetflow-open-surgeon', handler);
  }, []);

  // Resize handler
  useEffect(() => {
    const handler = () => { if (mapInstanceRef.current) mapInstanceRef.current.invalidateSize(); };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  return (
    <div
      ref={mapRef}
      style={{ width: '100%', height: 'calc(100vh - 57px)', cursor: 'crosshair' }}
    />
  );
};

// ── Main page component ───────────────────────────────────────────────────────
const UKPopulationExplorer = ({ navigateTo }) => {
  const { logout } = useAuth();

  const [selectedLocation, setSelectedLocation] = useState(null);
  const [radius, setRadius] = useState(10);          // always km
  const [tempRadius, setTempRadius] = useState(10);   // always km
  const [useMiles, setUseMiles] = useState(false);
  const [populationResult, setPopulationResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [error, setError] = useState(null);
  const [selectedNSSECClasses, setSelectedNSSECClasses] = useState(['all']);
  const [showNSSECPicker, setShowNSSECPicker] = useState(false);
  const [showAreas, setShowAreas] = useState(false);
  const [showClinics, setShowClinics] = useState(true);
  const [clinicMarkers, setClinicMarkers] = useState([]);
  const [vetGroups, setVetGroups] = useState([]);
  const [ownershipList, setOwnershipList] = useState([]);
  // clinic filters
  const [showClinicFilters, setShowClinicFilters] = useState(false);
  const [filterClinicTag, setFilterClinicTag] = useState('');
  const [filterClinicType, setFilterClinicType] = useState('');
  const [filterOwnershipType, setFilterOwnershipType] = useState('');
  const [filterOwner, setFilterOwner] = useState('');

  const handleLogout = () => { logout(); navigateTo('home'); };

  const handleClinicNavigate = (clinicId) => {
    const clinic = filteredClinicMarkers.find(c => c.id === clinicId);
    if (clinic?.name) sessionStorage.setItem('surgeons-db-clinic-name', clinic.name);
    sessionStorage.setItem('surgeons-db-clinic-filter', clinicId);
    sessionStorage.setItem('surgeons-db-return', 'uk-population');
    navigateTo('surgeons-database');
  };

  const handleSurgeonNavigate = (surgeonId, surgeonName) => {
    sessionStorage.setItem('surgeons-db-open-surgeon', surgeonId);
    if (surgeonName) sessionStorage.setItem('surgeons-db-open-surgeon-name', surgeonName);
    sessionStorage.setItem('surgeons-db-return', 'uk-population');
    navigateTo('surgeons-database');
  };

  // Fetch clinics + groups from API, then geocode postcodes
  useEffect(() => {
    const loadClinics = async () => {
      try {
        // Load groups, ownership and all clinics in parallel
        const [hRes, cRes, oRes] = await Promise.all([
          apiFetch(`/api/surgeons-db/hubs`),
          apiFetch(`/api/surgeons-db/clinics/all`),
          apiFetch(`/api/surgeons-db/ownership`),
        ]);
        if (!hRes.ok || !cRes.ok) return;
        const hubList = await hRes.json();
        const clinicList = await cRes.json();
        setVetGroups(hubList);
        if (oRes.ok) setOwnershipList(await oRes.json());

        // Build surgeon lookup from /clinics/all (already has surgeons attached)
        const surgeonsByClinicId = {};
        (clinicList || []).forEach(c => { surgeonsByClinicId[c.id] = c.surgeons || []; });

        // Fetch full clinic data with pagination to get addresses/phones
        let allClinics = [];
        let skip = 0;
        const limit = 500;
        while (true) {
          const r = await apiFetch(`/api/surgeons-db/clinics?skip=${skip}&limit=${limit}`);
          if (!r.ok) break;
          const d = await r.json();
          allClinics = allClinics.concat(d.items || []);
          if (allClinics.length >= d.total) break;
          skip += limit;
        }

        // Attach surgeons to each clinic
        allClinics = allClinics.map(c => ({ ...c, surgeons: surgeonsByClinicId[c.id] || [] }));

        // ── Geocode via postcodes.io bulk API (100 per batch) ────────────────
        const PC_RE = /[A-Z]{1,2}\d[\dA-Z]?\s*\d[A-Z]{2}/i;

        // Separate clinics that already have coords vs those that need geocoding
        const withCoords = allClinics.filter(c => c.lat && c.lng);
        const needsGeocode = allClinics.filter(c => !c.lat || !c.lng);

        // Show pre-geocoded clinics immediately
        setClinicMarkers(withCoords);

        // Extract postcodes for clinics that need geocoding
        const clinicPostcodes = needsGeocode.map(c => {
          const m = PC_RE.exec(c.address || '');
          return m ? m[0].replace(/\s/g, '').toUpperCase() : null;
        });

        // Bulk lookup in batches of 100
        const BATCH = 100;
        const coordMap = {}; // postcode -> { lat, lng }
        for (let i = 0; i < clinicPostcodes.length; i += BATCH) {
          const batch = clinicPostcodes.slice(i, i + BATCH).filter(Boolean);
          if (!batch.length) continue;
          try {
            const res = await fetch('https://api.postcodes.io/postcodes', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ postcodes: batch }),
            });
            if (res.ok) {
              const data = await res.json();
              (data.result || []).forEach(item => {
                if (item.result) {
                  coordMap[item.query.replace(/\s/g, '').toUpperCase()] = {
                    lat: item.result.latitude,
                    lng: item.result.longitude,
                  };
                }
              });
            }
          } catch {}
        }

        // For clinics still unresolved, try outcode fallback
        const outcodeMap = {};
        const unresolvedOutcodes = [...new Set(
          clinicPostcodes
            .filter(pc => pc && !coordMap[pc])
            .map(pc => pc.match(/^([A-Z]{1,2}\d[\dA-Z]?)/i)?.[1]?.toUpperCase())
            .filter(Boolean)
        )];
        await Promise.all(unresolvedOutcodes.map(async outcode => {
          try {
            const r = await fetch(`https://api.postcodes.io/outcodes/${encodeURIComponent(outcode)}`);
            const d = await r.json();
            if (d.status === 200 && d.result) {
              outcodeMap[outcode] = { lat: d.result.latitude, lng: d.result.longitude };
            }
          } catch {}
        }));

        // Build final geocoded list
        const geocoded = needsGeocode.map((clinic, idx) => {
          const pc = clinicPostcodes[idx];
          if (pc && coordMap[pc]) return { ...clinic, ...coordMap[pc] };
          const outcode = pc?.match(/^([A-Z]{1,2}\d[\dA-Z]?)/i)?.[1]?.toUpperCase();
          if (outcode && outcodeMap[outcode]) return { ...clinic, ...outcodeMap[outcode] };
          return { ...clinic, lat: null, lng: null };
        });

        setClinicMarkers([...withCoords, ...geocoded]);
      } catch (e) {
        // Backend not available — leave markers empty
      }
    };
    loadClinics();
  }, []);

  const nssecParam = selectedNSSECClasses.includes('all') ? 'all' : selectedNSSECClasses.join(',');
  const isFiltered = !selectedNSSECClasses.includes('all');

  // ── Derived: unique tags, clinic types, ownership types, owners ───────────
  const allClinicTags = [...new Set(clinicMarkers.flatMap(c => c.tags || []))].sort();
  const allClinicTypes = [...new Set(clinicMarkers.map(c => c.clinic_type).filter(Boolean))].sort();
  const allOwnershipTypes = [...new Set(ownershipList.map(o => o.type).filter(Boolean))].sort();
  const activeFilterCount = [filterClinicTag, filterClinicType, filterOwnershipType, filterOwner].filter(Boolean).length;

  const CLINIC_TYPE_LABELS = {
    small_animal: 'Small Animal', referral: 'Referral', equine: 'Equine',
    mixed: 'Mixed', exotic: 'Exotic', farm: 'Farm', emergency: 'Emergency', specialist: 'Specialist',
  };

  // ── Filtered clinic markers (applied to map + radius calc) ────────────────
  const filteredClinicMarkers = clinicMarkers.filter(c => {
    if (filterClinicTag && !(c.tags || []).includes(filterClinicTag)) return false;
    if (filterClinicType && c.clinic_type !== filterClinicType) return false;
    if (filterOwner && c.ownership_id !== filterOwner) return false;
    if (filterOwnershipType) {
      const own = ownershipList.find(o => o.id === c.ownership_id);
      if (!own || own.type !== filterOwnershipType) return false;
    }
    return true;
  });

  // ── Clinics + surgeons within radius ─────────────────────────────────────
  const clinicsInRadius = selectedLocation
    ? filteredClinicMarkers.filter(c => {
        if (!c.lat || !c.lng) return false;
        const R = 6371;
        const dLat = (c.lat - selectedLocation.lat) * Math.PI / 180;
        const dLng = (c.lng - selectedLocation.lng) * Math.PI / 180;
        const a = Math.sin(dLat/2)**2 + Math.cos(selectedLocation.lat * Math.PI/180) * Math.cos(c.lat * Math.PI/180) * Math.sin(dLng/2)**2;
        return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)) <= radius;
      })
    : [];
  const surgeonsInRadius = clinicsInRadius.reduce((sum, c) => sum + (c.surgeons?.length || 0), 0);

  const fetchPopulation = useCallback(async (loc, rad, nssec) => {
    if (!loc) return;
    setIsLoading(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/population-explorer/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lat: loc.lat, lng: loc.lng, radius_km: rad, nssec_class: nssec }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setPopulationResult(data);
    } catch (err) {
      setError('Failed to fetch population data. Make sure the backend is running.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedLocation) fetchPopulation(selectedLocation, radius, nssecParam);
  }, [selectedLocation, radius, nssecParam, fetchPopulation]);

  const handleMapClick = useCallback((lat, lng) => {
    setSelectedLocation({ lat, lng });
    setShowSearchResults(false);
  }, []);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setIsSearching(true);
    setShowSearchResults(true);
    try {
      const res = await apiFetch(`/api/population-explorer/location/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: searchQuery }),
      });
      setSearchResults(res.ok ? await res.json() : []);
    } catch {
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const selectResult = (result) => {
    setSelectedLocation({ lat: result.lat, lng: result.lng });
    setSearchQuery(result.outcode);
    setShowSearchResults(false);
  };

  const clearSelection = () => {
    setSelectedLocation(null);
    setPopulationResult(null);
    setSearchQuery('');
    setShowSearchResults(false);
  };

  const toggleNSSEC = (id) => {
    setSelectedNSSECClasses((prev) => {
      if (id === 'all') return ['all'];
      const without = prev.filter(c => c !== 'all');
      const already = without.includes(id);
      const updated = already ? without.filter(c => c !== id) : [...without, id];
      return updated.length === 0 ? ['all'] : updated;
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 shrink-0">
        <div className="max-w-full px-4 sm:px-6">
          <div className="flex justify-between items-center py-3">
            <div className="flex items-center space-x-3">
              <button onClick={() => navigateTo('staff-portal')} className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg">
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div className="w-9 h-9 bg-blue-600 rounded-lg flex items-center justify-center">
                <Map className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-900">UK Population Explorer</h1>
                <p className="text-xs text-gray-500">Census 2021 · Output Area Level · NS-SeC Classification</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowClinics(v => !v)}
                title={showClinics ? 'Hide vet clinics' : 'Show vet clinics'}
                className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
                  showClinics
                    ? 'bg-red-600 border-red-600 text-white hover:bg-red-700'
                    : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Stethoscope className="h-4 w-4" />
                <span className="hidden sm:inline">Vet Clinics</span>
              </button>
              <button onClick={handleLogout} className="flex items-center space-x-2 px-3 py-2 text-gray-600 hover:text-gray-900">
                <LogOut className="h-4 w-4" /><span className="text-sm">Logout</span>
              </button>
              <UserMenu navigateTo={navigateTo} />
            </div>
          </div>
        </div>
      </header>

      {/* Main: map + side panel */}
      <div className="flex flex-1 overflow-hidden" style={{ height: 'calc(100vh - 57px)' }}>

        {/* Side Panel */}
        <div className="w-80 bg-white border-r border-gray-200 flex flex-col shrink-0 overflow-hidden">
          <div className="flex-1 overflow-y-auto">

            {/* Search */}
            <div className="p-4 border-b border-gray-100">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Search Location</p>
              <div className="flex space-x-2">
                <div className="flex-1 relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSearch()}
                    placeholder="Postcode or area..."
                    className="w-full pl-8 pr-8 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {searchQuery && (
                    <button onClick={() => { setSearchQuery(''); setShowSearchResults(false); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                <button onClick={handleSearch} className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg">
                  <Search className="h-4 w-4" />
                </button>
              </div>

              {showSearchResults && (
                <div className="mt-2 border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
                  {isSearching ? (
                    <div className="p-3 text-sm text-gray-500 text-center">Searching…</div>
                  ) : searchResults.length > 0 ? (
                    searchResults.slice(0, 8).map((r, i) => (
                      <button key={i} onClick={() => selectResult(r)}
                        className="w-full flex items-center space-x-2 px-3 py-2.5 text-sm hover:bg-blue-50 border-b border-gray-100 last:border-0 text-left">
                        <MapPin className="h-3.5 w-3.5 text-blue-500 shrink-0" />
                        <span className="font-semibold text-gray-900">{r.outcode}</span>
                        {r.admin_district?.[0] && <span className="text-gray-400 text-xs truncate">{r.admin_district[0]}</span>}
                      </button>
                    ))
                  ) : (
                    <div className="p-3 text-sm text-gray-400 text-center">No results found</div>
                  )}
                </div>
              )}
            </div>

            {/* Radius */}
            <div className="p-4 border-b border-gray-100">
              <div className="flex justify-between items-center mb-2">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Search Radius</p>
                <div className="flex items-center gap-2">
                  <span className="text-base font-bold text-blue-600">
                    {useMiles ? (tempRadius * KM_TO_MI).toFixed(1) : tempRadius} {useMiles ? 'mi' : 'km'}
                  </span>
                  <button
                    onClick={() => setUseMiles(u => !u)}
                    className="text-xs px-2 py-0.5 rounded-full border border-gray-300 text-gray-500 hover:bg-gray-100 transition-colors"
                  >
                    {useMiles ? 'km' : 'mi'}
                  </button>
                </div>
              </div>
              <input
                type="range"
                min={1}
                max={100}
                step={1}
                value={tempRadius}
                onChange={e => setTempRadius(Number(e.target.value))}
                onMouseUp={e => setRadius(Number(e.target.value))}
                onTouchEnd={() => setRadius(tempRadius)}
                className="w-full accent-blue-600"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>{useMiles ? `${(1 * KM_TO_MI).toFixed(0)} mi` : '1 km'}</span>
                <span>{useMiles ? `${(50 * KM_TO_MI).toFixed(0)} mi` : '50 km'}</span>
                <span>{useMiles ? `${(100 * KM_TO_MI).toFixed(0)} mi` : '100 km'}</span>
              </div>
            </div>

            {/* Clinic Filters */}
            <div className="p-4 border-b border-gray-100">
              <button onClick={() => setShowClinicFilters(p => !p)} className="w-full flex items-center justify-between">
                <div className="flex items-center space-x-1.5">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Clinic Filters</p>
                  {activeFilterCount > 0 && (
                    <span className="text-xs bg-blue-600 text-white rounded-full px-1.5 py-0.5 font-bold">{activeFilterCount}</span>
                  )}
                </div>
                <div className="flex items-center space-x-1">
                  {activeFilterCount > 0 && (
                    <button onClick={e => { e.stopPropagation(); setFilterClinicTag(''); setFilterClinicType(''); setFilterOwnershipType(''); setFilterOwner(''); }}
                      className="text-xs text-red-500 hover:text-red-700 mr-1">Clear</button>
                  )}
                  {showClinicFilters ? <ChevronUp className="h-3.5 w-3.5 text-gray-400" /> : <ChevronDown className="h-3.5 w-3.5 text-gray-400" />}
                </div>
              </button>

              {showClinicFilters && (
                <div className="mt-3 space-y-3">

                  {/* Tag */}
                  {allClinicTags.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-400 mb-1.5">Source Tag</p>
                      <div className="flex flex-wrap gap-1.5">
                        {allClinicTags.map(tag => (
                          <button key={tag} onClick={() => setFilterClinicTag(filterClinicTag === tag ? '' : tag)}
                            className={`px-2.5 py-0.5 rounded-full text-xs font-medium border transition-colors ${
                              filterClinicTag === tag ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'
                            }`}>
                            {tag === 'ezyvet' ? 'EzyVet' : tag === 'simons' ? "Simon's" : tag}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Clinic Type */}
                  {allClinicTypes.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-400 mb-1.5">Clinic Type</p>
                      <div className="flex flex-wrap gap-1.5">
                        {allClinicTypes.map(type => (
                          <button key={type} onClick={() => setFilterClinicType(filterClinicType === type ? '' : type)}
                            className={`px-2.5 py-0.5 rounded-full text-xs font-medium border transition-colors ${
                              filterClinicType === type ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'
                            }`}>
                            {CLINIC_TYPE_LABELS[type] || type}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Ownership Type */}
                  {allOwnershipTypes.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-400 mb-1.5">Ownership Type</p>
                      <div className="flex flex-wrap gap-1.5">
                        {allOwnershipTypes.map(type => (
                          <button key={type} onClick={() => setFilterOwnershipType(filterOwnershipType === type ? '' : type)}
                            className={`px-2.5 py-0.5 rounded-full text-xs font-medium border transition-colors capitalize ${
                              filterOwnershipType === type ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'
                            }`}>
                            {type}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Owner */}
                  <div>
                    <p className="text-xs text-gray-400 mb-1.5">Owner</p>
                    <select
                      value={filterOwner}
                      onChange={e => setFilterOwner(e.target.value)}
                      className="w-full text-xs px-2 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white">
                      <option value="">All owners</option>
                      {ownershipList.map(o => (
                        <option key={o.id} value={o.id}>{o.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="text-xs text-gray-400 pt-1">
                    Showing <span className="font-semibold text-gray-700">{filteredClinicMarkers.length}</span> of {clinicMarkers.length} clinics
                  </div>
                </div>
              )}
            </div>

            {/* NS-SeC Filter */}
            <div className="p-4 border-b border-gray-100">
              <button onClick={() => setShowNSSECPicker(p => !p)} className="w-full flex items-center justify-between">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Socioeconomic Classes</p>
                <div className="flex items-center space-x-1">
                  <span className="text-xs text-blue-600 font-medium">
                    {selectedNSSECClasses.includes('all') ? 'All' : `${selectedNSSECClasses.length} selected`}
                  </span>
                  {showNSSECPicker ? <ChevronUp className="h-3.5 w-3.5 text-gray-400" /> : <ChevronDown className="h-3.5 w-3.5 text-gray-400" />}
                </div>
              </button>

              {showNSSECPicker && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {NSSEC_CLASSES.map(cls => {
                    const isSelected = cls.id === 'all' ? selectedNSSECClasses.includes('all') : selectedNSSECClasses.includes(cls.id);
                    return (
                      <button
                        key={cls.id}
                        onClick={() => toggleNSSEC(cls.id)}
                        title={cls.description}
                        className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                          isSelected ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'
                        }`}
                      >
                        {cls.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Results */}
            {populationResult && (
              <div className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Population Data</p>
                  <button onClick={clearSelection} className="text-xs text-red-500 hover:text-red-700 font-medium">Clear</button>
                </div>

                {isLoading ? (
                  <div className="flex items-center justify-center py-6 space-x-2 text-gray-500">
                    <RefreshCw className="h-4 w-4 animate-spin" /><span className="text-sm">Calculating…</span>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      <div className="bg-blue-50 rounded-lg p-2.5 text-center">
                        <div className="text-lg font-bold text-blue-700">{formatPop(populationResult.filtered_population)}</div>
                        <div className="text-xs text-gray-500 mt-0.5">{isFiltered ? 'Filtered' : 'Population'}</div>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-2.5 text-center">
                        <div className="text-lg font-bold text-gray-800">{populationResult.areas_count}</div>
                        <div className="text-xs text-gray-500 mt-0.5">Areas</div>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-2.5 text-center">
                        <div className="text-lg font-bold text-gray-800">
                          {isFiltered && populationResult.total_population > 0
                            ? `${((populationResult.filtered_population / populationResult.total_population) * 100).toFixed(0)}%`
                            : useMiles ? `${(radius * KM_TO_MI).toFixed(1)}mi` : `${radius}km`}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">{isFiltered ? 'of Total' : 'Radius'}</div>
                      </div>
                    </div>

                    {/* Vet clinics + surgeons in radius */}
                    {clinicMarkers.length > 0 && (
                      <div className="grid grid-cols-2 gap-2 mb-3">
                        <button
                          onClick={() => setShowClinics(true)}
                          className="bg-red-50 rounded-lg p-2.5 text-center hover:bg-red-100 transition-colors"
                          title="Toggle clinic markers on map">
                          <div className="text-lg font-bold text-red-700">{clinicsInRadius.length}</div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            Vet Clinics{activeFilterCount > 0 ? ` (filtered)` : ''}
                          </div>
                        </button>
                        <div className="bg-green-50 rounded-lg p-2.5 text-center">
                          <div className="text-lg font-bold text-green-700">{surgeonsInRadius}</div>
                          <div className="text-xs text-gray-500 mt-0.5">Surgeons</div>
                        </div>
                      </div>
                    )}

                    {isFiltered && populationResult.total_population > 0 && (
                      <div className="mb-3">
                        <div className="h-5 bg-gray-100 rounded-full overflow-hidden relative">
                          <div
                            className="h-full bg-blue-400 rounded-full transition-all duration-500"
                            style={{ width: `${Math.min((populationResult.filtered_population / populationResult.total_population) * 100, 100)}%` }}
                          />
                          <span className="absolute inset-0 flex items-center justify-center text-xs font-medium text-gray-700">
                            {formatPop(populationResult.filtered_population)} of {formatPop(populationResult.total_population)}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* NS-SeC breakdown */}
                    {populationResult.nssec_breakdown && !isFiltered && (
                      <div className="mb-3 space-y-1">
                        {NSSEC_CLASSES.filter(c => c.id !== 'all').map(cls => {
                          const count = populationResult.nssec_breakdown[cls.id] || 0;
                          const pct = populationResult.total_population > 0
                            ? (count / populationResult.total_population) * 100
                            : 0;
                          return (
                            <div key={cls.id} className="flex items-center space-x-2 text-xs">
                              <span className="w-28 truncate text-gray-600">{cls.label}</span>
                              <div className="flex-1 h-2 bg-gray-100 rounded-full">
                                <div className="h-full bg-blue-400 rounded-full" style={{ width: `${pct}%` }} />
                              </div>
                              <span className="w-8 text-right text-gray-500">{pct.toFixed(0)}%</span>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Postcode list */}
                    {populationResult.areas.length > 0 && (
                      <div>
                        <button onClick={() => setShowAreas(p => !p)} className="flex items-center justify-between w-full text-xs font-medium text-gray-500 mb-2">
                          <span>Postcode Districts ({populationResult.areas.length})</span>
                          {showAreas ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                        </button>
                        {showAreas && (
                          <div className="space-y-0.5">
                            {populationResult.areas.slice(0, 30).map((area, i) => (
                              <div key={i} className="flex items-center text-xs py-1 border-b border-gray-50">
                                <span className="w-14 font-semibold text-gray-800">{area.outcode}</span>
                                <span className="flex-1 text-green-600 font-medium">{formatPop(area.filtered_population ?? area.population)}</span>
                                <span className="text-gray-400">{useMiles ? `${(area.distance_km * KM_TO_MI).toFixed(1)} mi` : `${area.distance_km?.toFixed(1)} km`}</span>
                              </div>
                            ))}
                            {populationResult.areas.length > 30 && (
                              <div className="text-center text-xs text-gray-400 py-1">+{populationResult.areas.length - 30} more</div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="mt-3 flex items-start space-x-1.5 text-xs text-gray-400">
                      <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                      <span>{populationResult.data_source}</span>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Empty state */}
            {!populationResult && !isLoading && (
              <div className="p-6 text-center text-gray-400">
                <Map className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                <p className="text-sm font-medium text-gray-500">Click the map to explore</p>
                <p className="text-xs mt-1">Select any location to see population and socioeconomic data from ONS Census 2021</p>
              </div>
            )}
          </div>

          {/* Data source note */}
          <div className="px-4 py-2 border-t border-gray-100 bg-gray-50 shrink-0">
            <p className="text-xs text-gray-400">Data: ONS Census 2021 · postcodes.io</p>
          </div>
        </div>

        {/* Map area */}
        <div className="flex-1 relative" style={{ height: 'calc(100vh - 57px)' }}>
          <LeafletMap
            selectedLocation={selectedLocation}
            radius={radius}
            onMapClick={handleMapClick}
            populationAreas={populationResult?.areas || []}
            clinicMarkers={filteredClinicMarkers}
            showClinics={showClinics}
            vetGroups={vetGroups}
            ownershipList={ownershipList}
            onClinicNavigate={handleClinicNavigate}
            onSurgeonNavigate={handleSurgeonNavigate}
          />

          {/* Loading overlay on map */}
          {isLoading && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 flex items-center space-x-2 bg-white bg-opacity-95 rounded-lg px-4 py-2 shadow-md text-sm text-gray-700">
              <RefreshCw className="h-4 w-4 animate-spin text-blue-600" />
              <span>Calculating population…</span>
            </div>
          )}

          {/* Error overlay */}
          {error && (
            <div className="absolute top-4 left-4 right-4 flex items-center justify-between bg-red-600 text-white rounded-lg px-4 py-2.5 shadow-md text-sm">
              <span>{error}</span>
              <button onClick={() => setError(null)} className="ml-3"><X className="h-4 w-4" /></button>
            </div>
          )}

          {/* Click instruction */}
          {!selectedLocation && !isLoading && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center space-x-2 bg-white bg-opacity-90 rounded-full px-4 py-2 shadow-md text-sm text-gray-600 pointer-events-none">
              <MapPin className="h-4 w-4 text-blue-500" />
              <span>Click anywhere on the map to select a location</span>
            </div>
          )}

          {/* Clear button */}
          {selectedLocation && (
            <button
              onClick={clearSelection}
              className="absolute top-4 right-4 flex items-center space-x-1.5 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 shadow-sm text-sm text-gray-700"
            >
              <X className="h-4 w-4" /><span>Clear</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default UKPopulationExplorer;
